
import { useState, useRef } from "react";
import { UploadCloud, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";

interface CSVImportDropzoneProps {
  onFileSelect: (file: File | null) => void;
}

export const CSVImportDropzone = ({ onFileSelect }: CSVImportDropzoneProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (isValidFile(file)) {
        setSelectedFile(file);
        onFileSelect(file);
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (isValidFile(file)) {
        setSelectedFile(file);
        onFileSelect(file);
      }
    }
  };

  const isValidFile = (file: File): boolean => {
    const validExtensions = ['.csv', '.txt', '.tsv'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    
    if (!validExtensions.includes(fileExtension)) {
      alert('Please upload a CSV file.');
      return false;
    }
    
    return true;
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onFileSelect(null);
  };

  return (
    <div
      className={`border-2 border-dashed rounded-lg p-8 text-center ${
        isDragging ? 'border-primary bg-primary/5' : 'border-gray-300'
      } transition-all duration-200 ease-in-out`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".csv,.txt,.tsv"
        className="hidden"
      />
      
      {selectedFile ? (
        <div className="flex flex-col items-center space-y-4">
          <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
            <FileText className="h-6 w-6 text-green-600" />
          </div>
          <div>
            <p className="font-medium">{selectedFile.name}</p>
            <p className="text-sm text-muted-foreground">
              {(selectedFile.size / 1024).toFixed(2)} KB
            </p>
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" size="sm" onClick={clearSelectedFile}>
              Remove
            </Button>
            <Button size="sm" onClick={handleButtonClick}>
              Change File
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-50">
            <UploadCloud className="h-6 w-6 text-primary" />
          </div>
          <h3 className="mt-4 text-lg font-medium">Drag & drop your CSV file here</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            or click the button below to browse files
          </p>
          <Button className="mt-4" onClick={handleButtonClick}>
            Browse Files
          </Button>
          <p className="mt-4 text-xs text-muted-foreground">
            Supported formats: CSV, TSV, TXT (up to 10MB)
          </p>
        </>
      )}
    </div>
  );
};
